import React from 'react';
import { UserAvatar } from '@/components/user-avatar';
import { OrganizationAvatar } from '@/components/organization-avatar';
import { NotificationBell } from '@/components/notification-bell';
import { cn } from '@/lib/utils';

export interface PageHeaderLayoutProps {
  // Row 1
  pageTitle: string;
  pageSubtitle?: string;
  profilePhoto?: React.ReactNode; // Optional custom profile photo, defaults to UserAvatar
  
  // Row 2
  searchComponent?: React.ReactNode; // Search boxes
  ticketSummaryCards?: React.ReactNode; // Status cards (e.g. Open, In Progress)
  likeCount?: number; // Like count with icon
  
  // Row 3
  tabs?: React.ReactNode; // Tab navigation
  middleControls?: React.ReactNode; // Middle controls (between tabs and action buttons)
  actionButtons?: React.ReactNode; // Save/Cancel buttons, etc.
  rightRailContent?: React.ReactNode; // Optional compact info cards below avatar
  rightRailTop?: string; // Optional top offset for right rail content
  /** Optional max-width for Row 3 content (e.g. '900px') so action buttons align with a content column below (e.g. calendar page) */
  row3MaxWidth?: string;

  // Content
  children: React.ReactNode;

  // Optional className for header container
  className?: string;
}

export function PageHeaderLayout({
  pageTitle,
  pageSubtitle,
  profilePhoto,
  searchComponent,
  ticketSummaryCards,
  likeCount,
  tabs,
  middleControls,
  actionButtons,
  rightRailContent,
  rightRailTop = '148px',
  row3MaxWidth,
  children,
  className,
}: PageHeaderLayoutProps) {
  // Default to UserAvatar if no profile photo provided
  const profilePhotoElement = profilePhoto || <UserAvatar size="md" />;

  // Render like count with heart icon (icon on top, count below)
  const renderLikeCount = () => {
    if (likeCount === undefined || likeCount === null) return null;
    
    return (
      <div className="flex flex-col items-center gap-0.5">
        <img 
          src="/images/icons/icon_heart_outline.svg" 
          alt="Likes" 
          className="w-7 h-7"
        />
        <span 
          className="text-base font-semibold text-gray-700"
          style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
        >
          {likeCount}
        </span>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header Container with 3 Rows - relative so avatar can align to viewport right */}
      <header className={cn("relative bg-white shadow-sm", className)}>
        {/* Row 1: Page Title + Profile Photo */}
        <div className="pl-4 pr-28 h-[80px] flex items-center">
          <div className="flex items-center justify-between w-full gap-4">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              {pageSubtitle ? (
                <div className="flex flex-col justify-center min-w-0 w-full">
                  <h1 className="text-heading-page leading-tight whitespace-nowrap overflow-hidden text-ellipsis">{pageTitle}</h1>
                  <p className="text-sm text-slate-600 mt-0.5 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">{pageSubtitle}</p>
                </div>
              ) : (
                <div className="flex items-center min-w-0 w-full">
                  <h1 className="text-heading-page leading-tight whitespace-nowrap overflow-hidden text-ellipsis">{pageTitle}</h1>
                </div>
              )}
            </div>
            <div className="flex items-center flex-shrink-0">
              <NotificationBell />
              {/* Profile photo hidden for now - can be reactivated later */}
              {/* {profilePhotoElement} */}
            </div>
          </div>
        </div>
        {/* Avatar: w-20 matches sidebar; right-4 aligns with tab content */}
        <div className="absolute right-4 top-0 w-20 h-[80px] flex items-center justify-center pointer-events-auto">
          <OrganizationAvatar size="lg" />
        </div>
        {/* Like icon + count: vertically aligned with Row 2 (second section) */}
        <div className="absolute right-4 top-[80px] w-20 h-[63px] flex items-center justify-center pointer-events-auto">
          {renderLikeCount()}
        </div>
        {rightRailContent && (
          <div
            className="absolute right-28 min-h-[1px] flex flex-col items-end gap-2 pointer-events-auto"
            style={{ top: rightRailTop }}
          >
            {rightRailContent}
          </div>
        )}

        {/* Row 2: Search + Ticket Summary Cards */}
        {/* pr-28 matches Row 3 to account for right sidebar width */}
        <div className="pl-4 pr-28 h-[35px] flex items-center">
          <div className="flex items-center justify-between gap-4 w-full">
            {/* Left: Search Component */}
            <div className="flex-1">
              {searchComponent || <div />}
            </div>
            
            {/* Right: Ticket Summary Cards only (like count moved below avatar); min-w-0 so slot can shrink and inner horizontal scroll works */}
            <div className="flex items-center justify-end ml-auto min-w-0 max-w-full">
              {ticketSummaryCards}
            </div>
          </div>
        </div>

        {/* Row 3: Tabs + Middle Controls + Action Buttons */}
        {/* pr-28 (112px) accounts for right sidebar width (80px) + gap (16px) + content padding (16px). row3MaxWidth constrains content so buttons align with column below (e.g. calendar). */}
        <div className="pl-4 pr-28 h-[55px] flex items-center">
          <div
            className="flex items-center justify-between gap-4 w-full"
            style={row3MaxWidth ? { maxWidth: row3MaxWidth } : undefined}
          >
            {/* Left: Tabs */}
            <div className="flex-1">
              {tabs || <div />}
            </div>
            
            {/* Middle: Controls (only shown when provided) */}
            {middleControls && (
              <div className="flex items-center justify-end gap-4">
                {middleControls}
              </div>
            )}
            
            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {actionButtons}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
